import React, {
  useState,
  forwardRef,
  useEffect,
  useRef,
  createRef,
  useCallback,
  useMemo,
} from 'react'
import {Stack, Box, Label, Text, Button, Dialog, Flex} from '@sanity/ui'
import {set, useFormValue} from 'sanity'
import {useClient} from 'sanity'
import {ArrayOfObjectsInputProps} from 'sanity'
import {Select} from '@sanity/ui'
import ProductCard from './ProductCard'
import {getFrontendURL} from '../utils/environment'

// Generate a unique key for Sanity array items
const generateKey = () => Math.random().toString(36).substring(2, 10)

type ProductSkusProps = ArrayOfObjectsInputProps

interface Product {
  _key?: string
  fitmentAvailability: string[]
  sku: string
  name: string
  id: string
  front: boolean
  rear: boolean
  isDefaultFront: boolean
  isDefaultRear: boolean
  frontWarning?: {_ref: string; _type: 'reference'}
  rearWarning?: {_ref: string; _type: 'reference'}
}

interface Filter {
  id: string
  name: string
  code: string
  options: {label: string; value: string}[]
}

interface ConfigWarning {
  label: string
  value: string
}

const ProductSkus = forwardRef<HTMLDivElement, ProductSkusProps>((props, ref) => {
  const client = useClient()
  const {value, onChange} = props

  // Get document from form context (Sanity v4 approach)
  const document = useFormValue([]) as any

  // Get base URL from environment configuration (migrated from v2 feURL)
  const baseURL = getFrontendURL()

  /* ******************************************************************************************
  // Initialize component data and update function
  ****************************************************************************************** */
  const [isLoaded, setIsLoaded] = useState(false)
  const [data, setData] = useState<Product[]>([])

  const updateData = useCallback(
    (newValue: Product[] = []) => {
      setData(newValue)
      if (!isLoaded) {
        return
      }

      onChange(set(newValue))
    },
    [onChange, isLoaded],
  )

  /* ******************************************************************************************
  // Fitment Availability options
  ****************************************************************************************** */
  const [fitmentAvailability, setFitmentAvailability] = useState<{label: string; value: string}[]>(
    [],
  )
  const lastFitmentFilterId = useRef<string | undefined>(undefined)

  const getFitmentAvailability = useCallback(
    (_id?: string) => {
      // Skip if we've already fetched for this ID
      if (_id === lastFitmentFilterId.current) {
        return
      }

      if (!_id) {
        setFitmentAvailability([])
        lastFitmentFilterId.current = undefined
        return
      }

      lastFitmentFilterId.current = _id

      // Update existing data to clear fitment availability when filter changes
      const currentData = (value as any[]) || []
      updateData(currentData.map((item) => ({...item, fitmentAvailability: []})))

      client.fetch(`*[_id == "${_id}"][0].options`).then((options) => {
        if (!options) return
        const mappedOptions = options.map((option: string) => ({label: option, value: option}))
        setFitmentAvailability(mappedOptions)
      })
    },
    [client, value, updateData],
  )

  /* ******************************************************************************************
  // Config warnings
  ****************************************************************************************** */
  const [configWarnings, setConfigWarnings] = useState<ConfigWarning[]>([])

  const getConfigWarnings = useCallback(() => {
    try {
      client
        .fetch(`*[_type == "configuratorWarning"] | order(title asc) { 'label': title, 'value': _id }`)
        .then((options) => {
          setConfigWarnings(options)
        })
    } catch (e) {
      console.error('Error fetching config warnings:', e)
    }
  }, [client])

  /* ******************************************************************************************
  // Filters
  ****************************************************************************************** */
  const [allFilters, setAllFilters] = useState<Filter[]>([])

  const searchFilterCodes = useMemo(
    () => [
      'apex_wheel_design',
      'apex_wheel_width',
      'apex_wheel_diameter',
      'apex_wheel_bolt_pattern',
      'apex_wheel_center_bore',
    ],
    [],
  )

  const filterRefs = useRef<Record<string, React.RefObject<any>> | null>(null)

  // Initialize refs only once (not on every render)
  if (filterRefs.current === null) {
    filterRefs.current = {}
    searchFilterCodes.forEach((code) => {
      filterRefs.current![code] = createRef()
    })
    filterRefs.current.search = createRef()
  }

  const getAttributes = useCallback(async () => {
    try {
      const response = await fetch(`${baseURL}/api/sync/content/data-attributes`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const json = await response.json()
      const data = json?.data || json

      const filters = data?.attributes?.reduce((map: Filter[], attribute: any) => {
        if (!searchFilterCodes.includes(attribute.code)) {
          return map
        }

        // Remap options from object into array of objects with value and label properties
        attribute.options = Object.keys(attribute.options).map((key) => ({
          label: attribute.options[key],
          value: key,
        }))

        map.push(attribute)
        return map
      }, [])

      setAllFilters(filters || [])
    } catch (error) {
      console.error('Error fetching attributes:', error)
    }
  }, [baseURL, searchFilterCodes])

  /* ******************************************************************************************
  // Form section
  ****************************************************************************************** */
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [searchResults, setSearchResults] = useState<Product[]>([])

  const onFormSubmit = useCallback(async () => {
    setSearchError('')

    if (!filterRefs.current) {
      return
    }

    setSearching(true)

    const filters: Record<string, string> = {}

    const searchValue = filterRefs.current?.search?.current?.value?.trim?.()

    if (searchValue) {
      filters.sku = searchValue
    } else {
      searchFilterCodes.forEach((code) => {
        const filterValue = filterRefs.current?.[code]?.current?.value
        if (filterValue && filterValue !== '') {
          filters[code] = filterValue
        }
      })
    }

    const queryParams = new URLSearchParams(filters).toString()

    try {
      const response = await fetch(
        `${baseURL}/api/callbacks/search-products${queryParams ? `?${queryParams}` : ''}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      )

      const items = await response.json()

      if (items?.error) {
        setSearchError(items?.error)
      }

      if (items?.length) {
        const currentData = (value as any[]) || []
        const usedSkus = currentData.map((item) => item.sku) || []
        const results = items?.reduce((map: Product[], product: any) => {
          // Filter out already selected products
          if (usedSkus.includes(product.sku)) {
            return map
          }

          // Keep only relevant information
          map.push({
            _key: generateKey(),
            fitmentAvailability: [],
            sku: product.sku,
            name: product.name,
            id: product.id,
            front: true,
            rear: true,
            isDefaultFront: false,
            isDefaultRear: false,
          })
          return map
        }, [])

        if (!results.length) {
          setSearchError('There are no new products matching your search criteria.')
        }

        setSearchResults(results)
      }
    } catch (error) {
      setSearchError('Error searching for products')
      console.error('Search error:', error)
    }

    setSearching(false)
  }, [baseURL, searchFilterCodes, value])

  const onFormReset = useCallback(() => {
    if (!filterRefs.current) return
    for (const key in filterRefs.current) {
      if (key === 'search') {
        if (filterRefs.current[key].current) {
          filterRefs.current[key].current.value = ''
        }
        continue
      }
      if (filterRefs.current[key].current) {
        filterRefs.current[key].current.value = ''
      }
    }
  }, [])

  /* ******************************************************************************************
  // Dialog
  ****************************************************************************************** */
  const onDialogClose = useCallback(() => {
    setSearchResults([])
  }, [])

  const onNewProductUpdate = useCallback(
    (product: Product) => {
      // Replace old product with new one, keep others the same
      const products = searchResults.map((item) => (item.sku === product.sku ? product : item))
      setSearchResults(products)
    },
    [searchResults],
  )

  const onNewProductAdd = useCallback(
    (product: Product) => {
      // Filter out selected product from searchResults
      setSearchResults(searchResults.filter((item) => item.sku !== product.sku))

      // Push selected product to data
      const currentData = (value as any[]) || []
      updateData([
        ...currentData.map((item) => ({
          ...item,
          // Reset pre-selected values on all other items
          isDefaultFront: product.isDefaultFront ? false : item.isDefaultFront,
          isDefaultRear: product.isDefaultRear ? false : item.isDefaultRear,
        })),
        product,
      ])
    },
    [searchResults, value, updateData],
  )

  /* ******************************************************************************************
  // Product actions
  ****************************************************************************************** */
  const onProductUpdate = useCallback(
    (product: Product) => {
      // Replace old product with new one, keep others the same
      const currentData = (value as any[]) || []
      const products = currentData.map((item) => {
        const newItem = {
          ...item,
          // Reset pre-selected values on all other items
          isDefaultFront: product.isDefaultFront ? false : item.isDefaultFront,
          isDefaultRear: product.isDefaultRear ? false : item.isDefaultRear,
        }

        return item.sku === product.sku ? product : newItem
      })
      updateData(products)
    },
    [value, updateData],
  )

  const onProductRemove = useCallback(
    (product: Product) => {
      // Filter out selected product
      const currentData = (value as any[]) || []
      const products = currentData.filter((item) => item.sku !== product.sku)
      updateData(products)
    },
    [value, updateData],
  )

  useEffect(() => {
    getConfigWarnings()
    getAttributes()
    setIsLoaded(true)
  }, [getConfigWarnings, getAttributes])

  // Initialize data from existing value, ensuring all items have _key
  useEffect(() => {
    if (value && Array.isArray(value) && value.length > 0) {
      // Ensure all items have a _key
      const dataWithKeys = (value as any[]).map((item) => ({
        ...item,
        _key: item._key || generateKey(),
      }))
      setData(dataWithKeys)
    } else if (!value || (Array.isArray(value) && value.length === 0)) {
      setData([])
    }
  }, [value])

  // Use document._createdAt to load local data (legacy support)
  useEffect(() => {
    if (!document?._createdAt) return
    // Delay setting local data slightly
    setTimeout(() => {
      if (value && Array.isArray(value)) {
        // Ensure all items have a _key
        const dataWithKeys = (value as any[]).map((item) => ({
          ...item,
          _key: item._key || generateKey(),
        }))
        setData(dataWithKeys)
      }
    }, 100)
  }, [document?._createdAt, value])

  useEffect(() => {
    getFitmentAvailability(document?.fitmentFilter?._ref)
  }, [document?.fitmentFilter?._ref, getFitmentAvailability])

  return (
    <Stack gap={4} ref={ref}>
      <Box>
        <fieldset
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            alignItems: 'center',
            marginTop: '2rem',
          }}
        >
          {allFilters.length ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                width: '100%',
              }}
            >
              {allFilters.map((attribute) => (
                <Box key={attribute.id}>
                  <Label size={1} weight="medium">
                    {attribute.name}
                  </Label>
                  <Select
                    ref={filterRefs.current?.[attribute.code]}
                    name={attribute.code}
                    defaultValue=""
                  >
                    <option value="">Select {attribute.name.toLowerCase()}...</option>
                    {attribute.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Box>
              ))}
            </div>
          ) : (
            <Text>Loading filters...</Text>
          )}

          <Box style={{width: '100%', maxWidth: '400px'}}>
            <input
              type="text"
              name="search"
              placeholder="Search by SKUs (comma separated)"
              ref={filterRefs.current?.search}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
            />
          </Box>

          <Flex gap={2}>
            <Button type="button" disabled={searching} onClick={onFormSubmit} tone="primary">
              {searching ? 'Searching...' : 'Search Wheels'}
            </Button>

            <Button type="button" onClick={onFormReset} mode="ghost">
              Reset
            </Button>
          </Flex>

          {searchError && (
            <Text size={1} style={{color: 'red'}}>
              {searchError}
            </Text>
          )}
        </fieldset>

        {searchResults?.length ? (
          <Dialog
            header={`${searchResults?.length} products found`}
            id="search-results"
            onClose={onDialogClose}
            width={640}
            zOffset={1000}
          >
            <Box padding={4}>
              <Stack gap={3}>
                {searchResults.map((product) => (
                  <ProductCard
                    key={product.sku}
                    product={product}
                    configWarnings={configWarnings}
                    fitmentAvailability={fitmentAvailability}
                    onUpdate={onNewProductUpdate}
                    action={{
                      label: 'Add',
                      callback: () => onNewProductAdd(product),
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Dialog>
        ) : null}

        {data?.length ? (
          <Stack gap={3} marginTop={4}>
            <Text weight="medium" size={2}>
              Selected Wheels:
            </Text>
            <Stack gap={3}>
              {data.map((product) => (
                <ProductCard
                  key={product.sku}
                  product={product}
                  configWarnings={configWarnings}
                  fitmentAvailability={fitmentAvailability}
                  onUpdate={onProductUpdate}
                  action={{
                    label: 'Remove',
                    callback: () => onProductRemove(product),
                    tone: 'critical' as const,
                  }}
                />
              ))}
            </Stack>
          </Stack>
        ) : (
          <Box marginTop={4}>
            <Text weight="medium" size={2}>
              Add wheels:
            </Text>
          </Box>
        )}
      </Box>
    </Stack>
  )
})

ProductSkus.displayName = 'ProductSkus'

export default ProductSkus
